import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState} from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import {ActionMethod, ActionOrigin} from 'constants/AnalyticsConstants';
import ResourcePresenceView from 'nav/presence/ResourcePresenceView';
import DocumentActions from 'notebook/documents/DocumentActions';
import {DocumentHistoryButton} from 'notebook/documents/DocumentHistoryButton';
import DocumentStore from 'notebook/documents/DocumentStore';
import {
  checkUnsubmittedDataErrors,
  getUnsubmittedTableNames,
  UnsubmittedDataError,
} from 'notebook/reviews/disableAuthorActions';
import WorksheetStepStatusBadge from 'notebook/worksheets/steps/WorksheetStepStatusBadge';
import {
  StatusTypeToDisplayNameMapping,
  WorksheetTaskLoadingStateMapping,
  WorksheetTaskStatusType,
  WorksheetViewStateType,
} from 'notebook/worksheets/WorksheetConstants';
import {WorksheetContext} from 'notebook/worksheets/WorksheetContext';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeWorksheetStepCompletionEventMetadata,
  composeWorksheetStepTransitionEventMetadata,
  WORKSHEET_STEP_COMPLETION_ENDED_EVENT,
  WORKSHEET_STEP_COMPLETION_STARTED_EVENT,
  WORKSHEET_STEP_TRANSITION_STATUS_ENDED_EVENT,
  WORKSHEET_STEP_TRANSITION_STATUS_STARTED_EVENT,
} from 'performance/processWorksheetsEvents';
import {useProcedureWorksheetContext} from 'procedures/worksheets/ProcedureWorksheetContext';
import {getProcedureType} from 'procedures/worksheets/WorksheetUtils';
import {bulkCreateOrUpdateProcessTasks} from 'processes/dao/ProcessManagementDAO';
import getTestProps from 'test/lib/cypress/getTestProps';
import {HasAvatar} from 'ui-library/avatar/Avatar';
import {GRAY, GRAY_DARK, WHITE} from 'ui-library/Colors';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {StepTransitionStatusType} from '../steps/WorksheetStepButton';
import {optimisticStepStatusState} from './OptimisticStepStatusState';

export const EDIT_LOCKED_TEXT =
  'Editing is disabled because run has not started. Start run to unlock the Run steps.';

export const EDIT_DISABLED_TEXT =
  "Editing is disabled because you don't have access to the Recipes or Assays application.";

export const EDIT_RELOADING_TEXT =
  'Editing is disabled because the worksheet is currently loading the latest data.';

export const SAVING_STEP_TEXT = 'Saving step...';
export const STEP_HISTORY_TITLE = 'STEP HISTORY';
export const STEP_HISTORY_TOOLTIP_TEXT = 'Step history';
export const STEP_SAVE_TIMEOUT_MS = 120000;

interface WorksheetStepHeaderProps {
  status?: WorksheetTaskStatusType;
  name?: string;
  lastEditedUser?: HasAvatar;
  timestamp?: string;
  outStatuses?: Record<string, string>; // {[displayName]: id}
  taskIds?: Array<string>;
  enableStepTransitions?: boolean;
  locked: boolean;
  documentId: string;
}

export default function WorksheetStepHeader({
  name,
  status = WorksheetTaskStatusType.NOT_READY,
  lastEditedUser,
  timestamp,
  outStatuses,
  taskIds,
  enableStepTransitions,
  locked,
  documentId,
}: WorksheetStepHeaderProps): JSX.Element {
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const {refetch, viewer, worksheetViewState} = WorksheetContext.useContext();
  const {PMEIs, isProcedureFeatureEnabled} = useProcedureWorksheetContext();
  const [optimisticStepStatus, setOptimisticStepStatusState] =
    optimisticStepStatusState.useGlobalClientState(documentId);
  const updateTaskStatus = useCallback(
    async (submitOption: WorksheetTaskStatusType) => {
      const statusIdToSet = outStatuses?.[StatusTypeToDisplayNameMapping[submitOption].toUpperCase()];
      if (taskIds && statusIdToSet) {
        setOptimisticStepStatusState(WorksheetTaskLoadingStateMapping[submitOption] ?? null);
        try {
          await bulkCreateOrUpdateProcessTasks({
            tasks: taskIds?.map((taskId) => ({
              id: taskId,
              sourceType: 'worksheet',
              actionOrigin: ActionOrigin.WORKSHEET,
              actionMethod: ActionMethod.BUTTON_CLICK,
              statusId: statusIdToSet,
            })),
            showLoadingScreen: false,
          });
          await refetch();
        } finally {
          // Defer to make sure the refetch writes the new value in the Apollo cache.
          _.defer(() => {
            setOptimisticStepStatusState(null);
          });
        }
      }
    },
    [outStatuses, taskIds, setOptimisticStepStatusState, refetch]
  );

  const onStatusChange = useCallback(
    async (submitOption: StepTransitionStatusType) => {
      setIsChangingStatus(true);
      logProfilerTimingEvent(WORKSHEET_STEP_TRANSITION_STATUS_STARTED_EVENT);
      if (submitOption === WorksheetTaskStatusType.COMPLETED) {
        logProfilerTimingEvent(WORKSHEET_STEP_COMPLETION_STARTED_EVENT);
      }
      const note = DocumentStore.getDocument(documentId)?.documentVersion.entryVersion.note;
      // Error if trying to mark a step task as completed and there are any unsubmitted tables,
      // textbox changes, or cells are still loading.
      let unsubmittedDataError = UnsubmittedDataError.NONE;
      if (
        submitOption === WorksheetTaskStatusType.COMPLETED &&
        BenchlingConfig.WORKSHEET_SEND_REVIEW_REQUIRES_SUBMITTED_TABLES &&
        note !== undefined
      ) {
        unsubmittedDataError = checkUnsubmittedDataErrors(note);
      }

      let completionError = null;

      if (unsubmittedDataError !== UnsubmittedDataError.NONE) {
        completionError = 'Error occurred while submitting status';
        switch (unsubmittedDataError) {
          case UnsubmittedDataError.UNSUBMITTED_TABLES: {
            const names = getUnsubmittedTableNames(note);
            completionError =
              names.length > 0
                ? `Please submit all tables before completing a step: ${names.join(', ')}`
                : 'Please submit all tables before completing a step';
            break;
          }
          case UnsubmittedDataError.TABLE_CELLS_LOADING:
            completionError = 'Please wait for all tables cells to load before completing a step';
            break;
          case UnsubmittedDataError.UNSUBMITTED_TEXT_BOXES:
            completionError = 'Please submit all text boxes before completing a step';
            break;
          default:
            break;
        }
      }

      if (
        submitOption === WorksheetTaskStatusType.COMPLETED &&
        unsubmittedDataError === UnsubmittedDataError.NONE
      ) {
        // Trigger a document save to ensure the evaluation snapshot is persisted before completing the step
        DocumentActions.update(documentId, {$merge: {}}, {cidIdMap: null, bumpVersion: true});
        const timeoutPromise = new Promise<boolean>((resolve) => {
          setTimeout(() => {
            resolve(false);
          }, STEP_SAVE_TIMEOUT_MS);
        });
        const savePromise = DocumentActions.save(documentId);
        const savedSuccessfully = await Promise.race([savePromise, timeoutPromise]);

        if (!savedSuccessfully) {
          completionError = 'Cannot complete step while step is saving';
        }
      }

      if (completionError != null) {
        notifications.error(completionError);
      } else {
        await updateTaskStatus(submitOption);
        const procedureType = getProcedureType(PMEIs);
        const metadata = composeWorksheetStepTransitionEventMetadata(
          submitOption,
          BenchlingConfig.PROCEDURES_ENABLE_WORKFLOWS_ORCHESTRATION,
          status,
          taskIds,
          procedureType
        );
        const worksheetStepCompletionEventMetadata =
          composeWorksheetStepCompletionEventMetadata(procedureType);
        logProfilerTimingEvent(WORKSHEET_STEP_TRANSITION_STATUS_ENDED_EVENT, metadata);
        if (submitOption === WorksheetTaskStatusType.COMPLETED) {
          logProfilerTimingEvent(WORKSHEET_STEP_COMPLETION_ENDED_EVENT, worksheetStepCompletionEventMetadata);
        }
      }
      setIsChangingStatus(false);
    },
    [documentId, status, updateTaskStatus, taskIds, PMEIs]
  );

  return (
    <div
      className={css(styles.headerContainer)}
      {...getTestProps({component: 'WorksheetStepHeader', element: 'container'})}
    >
      <div className={css(styles.headerStepNameContainer)}>
        <div
          {...getTestProps({component: 'WorksheetStepHeader', element: 'name'})}
          className={css(styles.stepName)}
        >
          {name}
        </div>
        {locked && (
          <div {...getTestProps({component: 'WorksheetStepHeader', element: 'icon'})}>
            <Tooltip
              text={
                // If the worksheet is not loaded yet or if the step is transitioning, show the reloading text
                // If the procedure feature is enabled and the document is just locked, show the locked text
                // If the procedure feature is not enabled, show the feature disabled text
                worksheetViewState === WorksheetViewStateType.LOADED &&
                WorksheetTaskLoadingStateMapping[status]
                  ? isProcedureFeatureEnabled
                    ? EDIT_LOCKED_TEXT
                    : EDIT_DISABLED_TEXT
                  : EDIT_RELOADING_TEXT
              }
              placement={FloatingPlacement.TOP}
            >
              <BenchlingIcon icon="lock" className={css(styles.lockIcon)} />
            </Tooltip>
          </div>
        )}
      </div>
      <div className={css(styles.rightContainer)}>
        {!viewer && (
          <div className={css(styles.resourcePresenceContainer)}>
            <ResourcePresenceView resourceId={documentId} />
          </div>
        )}
        <DocumentHistoryButton
          buttonClassName={css(styles.iconButtonOverride)}
          documentId={documentId}
          title={STEP_HISTORY_TITLE}
          tooltipText={STEP_HISTORY_TOOLTIP_TEXT}
          {...getTestProps({component: 'StepHistoryButton', element: 'icon'})}
        />
        <WorksheetStepStatusBadge
          status={optimisticStepStatus ?? status}
          onSubmit={onStatusChange}
          lastEditedUser={lastEditedUser}
          timestamp={timestamp}
          locked={locked || !enableStepTransitions || isChangingStatus}
        />
      </div>
    </div>
  );
}

export const styles = StyleSheet.create({
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: '7px 16px',
    border: `1px solid ${GRAY}`,
    borderLeft: '0px',
    borderTop: '0px',
    background: WHITE,
  },
  headerStepNameContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
  },
  stepName: {
    fontSize: '14px',
    fontWeight: 600,
  },
  rightContainer: {
    display: 'flex',
  },
  lockIcon: {
    color: GRAY_DARK,
    width: '12px',
  },
  resourcePresenceContainer: {
    display: 'flex',
    marginRight: '8px',
    alignItems: 'center',
  },
  iconButtonOverride: {
    height: 32,
    width: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
